import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import {
	type BlastRadius,
	GetTaskBlastRadiusDocument,
} from "@/common/artemis-sdk/graphql";
import { useGqlClient } from "../use-gql-client";

/** What the hint judged, once it has something to say. */
export type BlastRadiusHint = {
	band: BlastRadius;
	flags: string[];
};

/**
 * Below this many characters there is nothing to judge, so no call is made.
 * The form itself only requires ten; "fix the db" is not a task description.
 * The server applies the same floor, because the server is what pays for a call.
 */
const MIN_LENGTH = 40;

/**
 * How much the text must change before it is worth re-judging. Adding a word never
 * moves a band; adding "and delete the old ones" does. This is the gate that stops
 * every edit from costing a request, and it is measured on normalised text so
 * reflowing whitespace is free.
 */
const MIN_DELTA = 20;

/** How long the typing has to stop before the text counts as a finished thought. */
const IDLE_MS = 2500;

/** Collapses whitespace so re-indenting or reflowing a description is not a change. */
export function normalise(text: string): string {
	return text.trim().replace(/\s+/g, " ");
}

/**
 * Character-level distance, cheap and good enough to decide "is this a different task
 * now". A real edit distance would be more accurate and is not worth the cycles: this
 * only has to separate "typed another word" from "added a destructive clause".
 */
function delta(a: string, b: string): number {
	if (a === b) return 0;
	let common = 0;
	const limit = Math.min(a.length, b.length);
	while (common < limit && a[common] === b[common]) common++;
	return Math.max(a.length, b.length) - common;
}

/** The pair of fields the classifier reads, as last sent or as currently typed. */
export type JudgedText = { name: string; description: string };

/**
 * Whether this text is worth spending a request on, given what was last judged.
 *
 * The whole cost story lives here: too short is free to reject, and an edit smaller
 * than the delta is not going to move a band. Exported so the policy can be tested
 * without a component, because it is the part that decides the bill.
 *
 * The floor applies to the description alone, since a long name over a one-line
 * description is not worth judging and the server rejects it anyway. The delta is
 * summed across both fields, because the name reaches the model too: leaving it out
 * made every keystroke in the Name field an ungated request.
 *
 * The two fields are measured separately rather than concatenated. `delta` compares a
 * common prefix, so with the name joined on the front a single character typed into it
 * would look like the whole description had been rewritten.
 *
 * @param last The name and description last sent, both empty if none.
 * @param name The normalised task name.
 * @param description The normalised task description.
 * @returns True when the text should be sent to the classifier.
 */
export function shouldJudge(
	last: JudgedText,
	name: string,
	description: string,
): boolean {
	if (description.length < MIN_LENGTH) return false;
	const changed = delta(last.name, name) + delta(last.description, description);
	return changed >= MIN_DELTA;
}

/**
 * Decides what text is worth sending to the classifier, and when.
 *
 * The rungs, in order: nothing below the length floor; then 2.5s after typing stops
 * if enough changed; and immediately on blur, so someone who writes the whole thing
 * and clicks away does not wait out the timer. There is deliberately no rung on
 * submit — the hint must never sit anywhere near the create path.
 *
 * `commit` is also handed back raw so a person can ask for a judgment now instead of
 * waiting out the idle timer. It carries no gate of its own: the caller decides when
 * asking is worth it, which is the whole point of a manual re-check.
 *
 * @returns The name and description currently worth judging, a blur handler, and the
 * force-commit behind the manual re-check.
 */
function useSettledText(name: string, description: string) {
	// Both fields are snapshotted together, and this doubles as the record of what was
	// last sent. Reading the live name instead would put it straight into the query key,
	// so every keystroke in the Name field would be a request with no gate in front.
	const [settled, setSettled] = useState<JudgedText>({
		name: "",
		description: "",
	});

	const currentName = normalise(name);
	const currentDescription = normalise(description);
	const worthJudging = shouldJudge(settled, currentName, currentDescription);

	const commit = useCallback(() => {
		setSettled({ name: currentName, description: currentDescription });
	}, [currentName, currentDescription]);

	useEffect(() => {
		if (!worthJudging) return;
		const timer = setTimeout(commit, IDLE_MS);
		return () => clearTimeout(timer);
	}, [worthJudging, commit]);

	const onBlur = useCallback(() => {
		if (worthJudging) commit();
	}, [worthJudging, commit]);

	return {
		settled,
		onBlur,
		commit,
		// Whether the field has been written in at all, as opposed to having enough to
		// judge. The panel needs the difference: a blank form shows nothing, a half-written
		// one shows the control and asks for more detail.
		hasAnyText: currentDescription.length > 0,
		// Whether there is anything to judge at all. Goes false when the wizard is
		// reopened and the form has been reset: the Drawer keeps its children mounted, so
		// `settled` survives a close and would otherwise still be on screen.
		hasText: currentDescription.length >= MIN_LENGTH,
		// True while the text has moved far enough that the settled answer no longer
		// describes it. An answer is kept on screen through edits too small to re-judge,
		// because it is still true of the text; once the text has changed enough to earn
		// a fresh judgment, the old one is stale and must not be shown as if current.
		stale: worthJudging,
	};
}

/**
 * Judges what a task would touch, from the name and description typed so far.
 *
 * Advisory only. A null result means "show nothing" and is the normal response when
 * the feature is switched off, the text is too short, or the provider is unreachable.
 * Nothing here can fail task creation: it is a separate query that `createTask` never
 * calls.
 *
 * @returns The hint or null, whether a judgment is in flight, the blur handler to
 * attach to the description field, and the manual re-check with the flag saying whether
 * pressing it would do anything.
 */
export function useTaskBlastRadius(
	name: string,
	description: string,
	environmentId: string | null,
) {
	const gqlFetch = useGqlClient();
	const { settled, onBlur, commit, hasAnyText, hasText, stale } =
		useSettledText(name, description);

	const query = useQuery({
		queryKey: [
			"task-blast-radius",
			settled.name,
			settled.description,
			environmentId,
		],
		enabled: settled.description.length >= MIN_LENGTH,
		// The answer for a given text does not change, and the model is not fully
		// deterministic, so re-asking would risk showing a different band for the same
		// words. Cache for the session and never refetch on focus.
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: 30 * 60 * 1000,
		refetchOnWindowFocus: false,
		retry: false,
		queryFn: async (): Promise<BlastRadiusHint | null> => {
			const result = await gqlFetch(GetTaskBlastRadiusDocument, {
				input: {
					name: settled.name,
					description: settled.description,
					environmentId,
				},
			});
			const hint = result.taskBlastRadius;
			return hint ? { band: hint.band, flags: [...hint.flags] } : null;
		},
	});

	// The last answer is held rather than read off the live query: `query.data` follows the
	// query key, which moves the moment the text is re-committed, so the panel would blink
	// out on every re-check.
	const [held, setHeld] = useState<BlastRadiusHint | null>(null);

	useEffect(() => {
		if (query.data) setHeld(query.data);
	}, [query.data]);

	// Clearing the form clears the answer. The Drawer keeps its children mounted, so
	// without this the next task opens showing the previous one's verdict.
	useEffect(() => {
		if (!hasText) setHeld(null);
	}, [hasText]);

	const showable = hasText && !query.isError;

	// The control is always live, so it has to always do something. Two different things,
	// because the query is keyed on the settled text: new text only needs committing and
	// the key change fetches on its own, while unchanged text is already cached forever
	// (see staleTime) and needs an explicit refetch to get a fresh answer at all.
	const { refetch } = query;
	const evaluate = useCallback(() => {
		if (stale) {
			commit();
			return;
		}
		void refetch();
	}, [stale, commit, refetch]);

	return {
		hint: showable ? held : null,
		// Whether to show the panel at all. Anything written in the description earns it,
		// because the control has to be reachable before the first answer exists — waiting
		// for one is what made the check impossible to ask for.
		visible: hasAnyText,
		// False while the description is below the classifier's floor, which the panel says
		// locally rather than spending a request the server rejects on the same floor.
		hasEnoughText: hasText,
		loading: hasText && (stale || query.isFetching),
		onDescriptionBlur: onBlur,
		// Judge the text as it stands, without waiting out the idle timer.
		evaluate,
		// True when the description has moved on from what the shown answer describes. The
		// answer is kept on screen rather than blanked, because a panel that vanishes mid-edit
		// is what made this hard to use — but it must not read as current while it is not.
		stale: showable && held !== null && stale,
	};
}
